import React from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { GatsbyImage } from 'gatsby-plugin-image';
import { useToggle } from 'react-use';

import useUserContext from '../hooks/use-user-context';
import { createOptionMap } from '../utilities/misc';
import { rem } from '../utilities/style';
import { colors } from '../theme/colors';
import { fontWeights } from '../theme/type';

const Container = styled.div`
  max-width: ${rem(880)};
`;

const VideoWrapper = styled.div`
  position: relative;
  padding-bottom: 56.25%;
  background: ${colors.gray['600']};
  aspect-ratio: 16/9;
  background-color: initial;
  box-shadow: unset;
  overflow: hidden;
  position: inherit;
  transform: translateY(0);

  ${({ videoBorder }) => (
    videoBorder
    && css`
      box-shadow: 0 0 0 1px rgba(0, 0, 0, .10)
    `
  )}
`;

const VideoIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const VideoThumbnail = styled(GatsbyImage)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 99%;
`;

const PlayButtonTriangle = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  transform: translate(-50%, -50%) translateX(10%);
  border-style: solid;
  border-width: 14px 0 14px 24px;
  color: ${colors.gray['600']};
  border-color: transparent transparent transparent currentColor;
`;

const PlayButton = styled.button`
  position: absolute;
  border: none;
  padding: 0;
  cursor: pointer;
  background: none;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  &[disabled] {
    pointer-events: none;

    ${PlayButtonTriangle} {
      color: ${colors.gray['300']};
    }
  }

  &:focus,
  &:hover {
    &::before {
      transform: translate(-50%, -50%) scale(1.1);
    }

    ${PlayButtonTriangle} {
      color: ${colors.blue['500']};
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: ${rem(65)};
    height: ${rem(65)};
    border-radius: 50%;
    background: ${colors.blue['50']};
    transition: transform .2s ease;
    box-shadow: 0 0 12px rgba(0, 0, 0, .5);
    pointer-events: none;
  }
`;

const Title = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  font-size: 1rem;
  font-weight: ${fontWeights.semiBold};
  color: ${colors.white};
  padding: ${rem(50)} ${rem(15)} ${rem(10)};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  text-shadow: 0 0 5px rgba(0, 0, 0, .3);
  background-image: linear-gradient(to top, rgba(0, 0, 0, .7), transparent);
  pointer-events: none;
`;

const getYoutubeEmbedUrl = (url) => {
  const isShortUrl = url.includes('youtu.be');
  const normalizedUrl = url.replace('youtube.com/embed/', 'youtube.com/watch?v=');
  const urlParts = new URL(normalizedUrl);
  const params = new URLSearchParams(urlParts.search);
  const videoId = isShortUrl ? urlParts.pathname.split('/')[1] : params.get('v');

  return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
};

const VideoPlayer = ({
  url,
  title,
  thumbnailUrl,
  thumbnailImage,
  videoBorder,
  description,
  uploadDate
}) => {
  const data = useStaticQuery(graphql`
    query VideoPlayer {
      allContentfulOptionSet(filter: {name: {eq: "video_player"}}) {
        nodes {
          ...OptionSetFragment
        }
      }
    }
  `);

  const { language } = useUserContext();
  const options = createOptionMap(data.allContentfulOptionSet.nodes, language);
  const [isVisible, toggleIsVisible] = useToggle(false);
  const isYoutube = url.includes('youtube.com') || url.includes('youtu.be');
  const isBilibili = url.includes('bilibili.com');

  let videoUrl = url;

  if (isYoutube) {
    videoUrl = getYoutubeEmbedUrl(url);
  } else if (isBilibili) {
    videoUrl = `${url}&autoplay=1`;
  }

  return (
    <Container itemScope itemType="http://schema.org/VideoObject">
      <meta itemProp="name" content={title} />
      <meta itemProp="description" content={description} />
      <meta itemProp="thumbnailUrl" content={thumbnailUrl} />
      <meta itemProp="uploadDate" content={uploadDate} />
      <meta itemProp="embedUrl" content={videoUrl} />
      <VideoWrapper videoBorder={videoBorder}>
        {thumbnailImage && (
          <VideoThumbnail image={thumbnailImage} alt={title} />
        )}
        <PlayButton
          type="button"
          onClick={toggleIsVisible}
          aria-label={options['video_player.play_button_label']}
          disabled={isVisible}
        >
          <PlayButtonTriangle />
        </PlayButton>
        {isVisible && (
          <VideoIframe
            title={title}
            src={videoUrl}
            frameBorder="0"
            allowFullScreen
            allow="autoplay; fullscreen"
          />
        )}
        {!isVisible && title && <Title>{title}</Title>}
      </VideoWrapper>
    </Container>
  );
};

VideoPlayer.propTypes = {
  url: PropTypes.string.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  videoBorder: PropTypes.bool,
  thumbnailUrl: PropTypes.string,
  thumbnailImage: PropTypes.shape({
    file: PropTypes.shape({
      url: PropTypes.string.isRequired
    }),
    // eslint-disable-next-line react/forbid-prop-types
    gatsbyImageData: PropTypes.object
  }),
  uploadDate: PropTypes.string
};

export default VideoPlayer;
